import React, { useEffect, useState } from 'react';
import { SurveyData, User } from '../types';
import { getSurveys, deleteSurvey } from '../services/storage';
import { Edit, Search, Loader, Trash2 } from 'lucide-react';
import SurveyForm from './SurveyForm';
import SecureActionModal from './SecureActionModal';
import { useToast } from './Toast';

interface MySubmissionsProps {
  user: User;
}

const MySubmissions: React.FC<MySubmissionsProps> = ({ user }) => {
  const [submissions, setSubmissions] = useState<SurveyData[]>([]);
  const [editingSurvey, setEditingSurvey] = useState<SurveyData | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<string | null>(null);
  const { addToast } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      // OPTIMIZATION: Pass userId to fetch ONLY this user's submissions from Firestore
      const userSurveys = await getSurveys({ userId: user.id });
      setSubmissions(userSurveys);
    } catch (e) {
      console.error(e);
      addToast("Failed to load submissions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user.id]);

  const handleEditSuccess = () => {
    setEditingSurvey(null);
    loadData();
  };

  const initiateDelete = (id: string) => {
    setSurveyToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (surveyToDelete) {
      try {
        await deleteSurvey(surveyToDelete);
        addToast("Survey deleted successfully", "success");
        await loadData();
      } catch (e) {
        addToast("Failed to delete survey", "error");
      }
    }
  };

  if (editingSurvey) {
    return (
      <div>
        <button onClick={() => setEditingSurvey(null)} className="mb-4 text-emerald-600 hover:underline flex items-center">
          &larr; Back to List
        </button>
        <SurveyForm user={user} onSuccess={handleEditSuccess} initialData={editingSurvey} />
      </div>
    );
  }

  if (loading) return <div className="p-10 text-center"><Loader className="animate-spin inline mr-2"/> Loading...</div>;

  const filtered = submissions.filter(s => {
    const headName = s.sectionA?.headName || '';
    const houseName = s.sectionA?.houseName || '';
    const term = search.toLowerCase();
    
    return headName.toLowerCase().includes(term) || houseName.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">My Submissions</h2>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Search..." 
            className="pl-10 p-2 border rounded shadow-sm focus:ring-emerald-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(survey => (
          <div key={survey.id} className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-100 relative group">
            <div className="flex justify-between items-start mb-2">
              <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded">#{survey.sectionA?.serialNo || 'N/A'}</span>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingSurvey(survey)}
                  className="text-gray-400 hover:text-blue-600 p-1"
                  title="Edit"
                >
                  <Edit size={18} />
                </button>
                <button 
                  onClick={() => initiateDelete(survey.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-1">{survey.sectionA?.headName || <span className="text-gray-400 italic">(No Name)</span>}</h3>
            <p className="text-gray-500 text-sm mb-2">{survey.sectionA?.houseName || '-'}</p>
            <div className="text-xs text-gray-400 grid grid-cols-2 gap-2 mt-4 pt-4 border-t">
              <div>Ward: {survey.sectionA?.wardNo || '-'}</div>
              <div>Members: {survey.sectionB?.length || 0}</div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
             No submissions found.
          </div>
        )}
      </div>

      <SecureActionModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Survey Response"
        message="Are you sure you want to delete this survey response? This action cannot be undone. Please enter your credentials to confirm."
      />
    </div>
  );
};

export default MySubmissions;